<template>
    <ThemeLoader>
        <div v-if="Username ===''" id="app" style="background-color: var(--background-primary);">
            <Login style="margin:0 auto; padding:50px" />
        </div>
        <div v-else-if="ServerSelectionNeeded" style="background-color: var(--background-primary);">
            <ChooseServer style="margin:0 auto; padding:50px" />
        </div>

        <div v-else-if="isAlmView">
            <LandscapeManagement></LandscapeManagement>
        </div>

        <div v-else id="app">
            <Header v-if="!IsEmbedded" />
            <Breadcrumb :style="{ top: IsEmbedded ? '0px' : '46px' }" />
            
            <Sidebar_v2 :style="{ top: IsEmbedded ? '40px' : '86px' }" @sidebar-collapsed="onSidebarCollapsed"/>

            <div id="main" style="background-color: var(--background-primary);" :style="{ top: IsEmbedded ? '40px' : '86px', left: sidebarCollapsed ? '50px' : '156px' }">
                <ChooseTenant v-if="Tenant === null" />
                <router-view v-else />
            </div>
            <div id="PopupsContainer">
                <WindowsTab v-for="tab in Tabs" :key="tab.Key" :TabData="tab" />
            </div>
            <portal-target name="DialogsContainer" multiple />
        </div>
    </ThemeLoader>

</template>

<script lang="ts">
    import { Component, Watch, Vue as vue } from 'vue-facing-decorator';

    import 'uicomponents/dist/style.css';
    import '@vue-js-cron/light/dist/light.css';
    import 'ag-grid-community/styles/ag-grid.css';
    import 'ag-grid-community/styles/ag-theme-alpine.css';
    import '@/assets/uicomponent-theme-override.css';

    import ThemeLoader from './components/ThemeLoader.vue';
    import Header from './components/Header.vue';
    import Breadcrumb from './components/Breadcrumb.vue';
    import Sidebar_v2 from './components/Sidebar_v2.vue';
    import Login from './components/Login.vue';
    import ChooseTenant from './components/ChooseTenant.vue';
    import ChooseServer from './components/ChooseServer.vue';
    import WindowsTab from './components/helpers/WindowsTab.vue';
    import * as api from './api/ApiClient';
    import { Localizer } from './api/Localizer';
    import { Portal, PortalTarget } from 'portal-vue';
    import { WindowsTabData } from './api/WindowsTabData';
    import LandscapeManagement from "@/components/pages/landscape/LandscapeManagement.vue";
    import { isEmbedded } from './utils/Utils';
    // import { useLDFlag, useLDReady, useLDClient } from 'launchdarkly-vue-client-sdk';

    @Component({
        components: {
            LandscapeManagement,
            Header, Breadcrumb, Sidebar_v2, Login, ChooseTenant, ChooseServer, ThemeLoader, PortalTarget, WindowsTab
        }
    })
    export default class App extends vue {
        private Localizer = new Localizer(this.$store);
        private sidebarCollapsed: boolean = false;
        private iframeObserver: MutationObserver | null = null;

        // private ldReady = useLDReady();
        // private ldClient = useLDClient();

        get isAlmView() {
            return this.$store.getters['alm/isAlmView'];
        }
        get Username(): string {
            return this.$store.state.security.UserName;
        }
        get Tenants(): api.Tenant[] | undefined {
            return this.$store.state.security.Tenants;
        }
        get Tenant(): api.Tenant | undefined {
            return this.$store.state.security.Tenant;
        }
        set Tenant(tenant: api.Tenant | undefined) {
            this.$store.commit('security/SetTenant', tenant);
        }

        get ServerSelectionNeeded() {
            return this.$store.state.security.ServerSelectionNeeded;
        }

        get Tabs(): WindowsTabData[] | undefined {
            return this.$store.state.windowstabs.Tabs;
        }
        get IsEmbedded(): boolean {
            return isEmbedded();
        }
            

        //method to handle sidebar collapse event
        onSidebarCollapsed(event: { isCollapsed: boolean, width: number }) {
            this.sidebarCollapsed = event.isCollapsed;
        }

        async mounted() {
            this.$nextTick(() => {
                window.addEventListener('newversiondownloaded', async () => {
                    await this.$store.dispatch('ShowToastCustom', {
                        notification: new api.ToastNotification({
                            message: this.Localizer.Localize('AppVue_New_Version_Available'),
                            type: api.ToastType.Info
                        }),
                        options: { timeout: false }
                    });
                });

                window.addEventListener('newversiondownloading', async () => {
                    await this.$store.dispatch('ShowToast', new api.ToastNotification({
                        message: this.Localizer.Localize('AppVue_New_Version_Downloading'),
                        type: api.ToastType.Info
                    }));
                });

                window.addEventListener('resize', this.onResize);
                if( this.IsEmbedded) window.addEventListener('beforeunload', this.handleBeforeUnload);
                this.onResize();
            });

            //not sure why this.$route.query doesn't always contain the proper values.
            const overrideLang = localStorage.getItem('UserLocaleOverride');
            let userLang = overrideLang == undefined ? 'en' : overrideLang;
            let queryString = window.location.search;
            const queryStringHasLang = queryString != null && queryString.indexOf('lang=') > 0;
            if (queryStringHasLang) {
                queryString = queryString.substring(queryString.indexOf('lang=') + 'lang='.length);
                if (queryString.indexOf('&') > 0) {
                    queryString = queryString.substring(0, queryString.indexOf('&'));
                }
                this.$store.dispatch('SetLocalizationLanguage', queryString);
            }
            else {
                if (overrideLang == undefined) {
                    if (navigator.languages && navigator.languages.length > 0) {
                        userLang = navigator.languages[0];
                    }
                    else {
                        userLang = navigator.language || 'en';
                    }
                }
            }
            this.$store.dispatch('SetLocalizationLanguage', userLang);
            // add a check to inspect auth in route query. If value exists, set it in localStorage.
            if( this.$route.query["auth"]) {
                localStorage.setItem("auth", this.$route.query["auth"] as string);
            }
        }

        @Watch('Tenants') async OnTenantsChange(val: api.Tenant[] | undefined) {
            await this.InitializeTenant(val);
        }

        async InitializeTenant(val: api.Tenant[] | undefined) {
            if (this.Tenant == null) {
                let queryString = decodeURIComponent(window.location.search);
                if (queryString != null && queryString.indexOf('tenant=') > 0) {
                    queryString = queryString.substring(queryString.indexOf('tenant=') + 'tenant='.length);
                    if (queryString.indexOf('&') > 0) {
                        queryString = queryString.substring(0, queryString.indexOf('&'));
                    }
                    // eslint-disable-next-line no-console
                    console.log(`Tenant found. ${queryString}`);

                    for (let i = 0; i < val!.length; i++) {
                        if (val![i].name == queryString) {
                            await this.$store.dispatch('security/Login', queryString);
                            return;
                        }
                    }
                }

                const storedTenantName = localStorage.getItem('tenantName');
                if (storedTenantName !== null && this.Tenants) {
                    const tenant = this.Tenants.find(t => t.name === storedTenantName);
                    if (tenant) {
                        await this.$store.dispatch('security/SelectTenant', tenant);
                    }
                }
            }
        }
        private getQueryStringValue(paramName: string): string | null {
            const urlParams = new URLSearchParams(window.location.search);
            const value = urlParams.get(paramName);
            console.log(`Query string value for ${paramName}: ${value}`);
            return value;
        }
        @Watch('Tenant') async OnTenantChange(val: api.Tenant | undefined) {
            if (val && val.name) {
                localStorage.setItem('tenantName', val.name);
            }
            else {
                localStorage.setItem('tenantName', '');
            }
            // if (this.ldClient && val) {
            //     this.ldClient?.identify({
            //         kind: 'user',
            //         key: 'fccr',
            //         tenant: val?.name?.toLowerCase(),
            //     });
            // }

        }

        handleBeforeUnload() {
            console.log('Logging out user before unload (from beforeunload event)');
            this.$store.dispatch('security/LogOut');
        }
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
            if( this.IsEmbedded) window.removeEventListener('beforeunload', this.handleBeforeUnload);
        }

        onResize() {
            this.$store.commit('SetWindowWidth', window.innerWidth);
            this.$store.commit('SetWindowHeight', window.innerHeight);
        }
    }
</script>

<style>
    #app {
        font-family: proxima-nova;
        font-size: 14px;
        color: var(--foreground-primary) !important;
        padding: 0px;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
    }

    #main {
        position: absolute;
        top: 86px;
        right: 0;
        bottom: 0;
        left: 156px;
        overflow-y: auto;
        scroll-behavior: smooth;
        transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* Add smooth transition for left position */
    }

    #PopupsContainer {
        position: absolute;
        top: 86px;
        right: 0;
        bottom: 0;
        left: 60px;
        pointer-events: none;
        overflow: hidden;
        z-index: 9999;
    }

        #PopupsContainer > * {
            pointer-events: all;
        }

    input, select, textarea {
        background-color: var(--background-primary);
        color: var(--foreground-primary);
        stroke: var(--thin-border);
    }

    .page-enter-active, .page-leave-active {
        transition: all .1s ease;
    }

    .page-leave-to {
        transform: translateX(-10px);
        opacity: 0;
    }

    .page-enter-from {
        transform: translateX(10px);
        opacity: 0;
    }

    hr {
        box-sizing: content-box;
        height: 0;
        overflow: visible;
        margin-top: 1rem;
        margin-bottom: 1rem;
        border: 0;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
    }

    .btn {
        display: inline-block;
    }
</style>
